/**
 * Landing a drawn shape's points exactly on the plan. The plan is only asked about one point at
 * a time — where does this corner belong? — never what the whole shape should be, so a snap can
 * only ever make what the person drew more exact.
 *
 * A corner goes to the plan's corner beneath it: two of the plan's lines running the ways the
 * shape's two sides leave the corner, crossing within reach (an L, a T, a crossing, or two lines
 * that stop short of meeting). It lands on the lines' outer ink edges — the side away from the
 * shape — the way every mark's border sits. Failing a corner, a point beside one line whose way
 * matches a side lands on that line's outer edge.
 */
import type { Vec } from '../../shared/doc.ts';
import type { IntentEngine } from '../vision/engine.ts';
import { edgeOffset, inkEdge, lumaAt } from '../vision/ink.ts';

export interface Snap {
  p: Vec;
  kind: 'corner' | 'line';
}

interface LocalLine {
  q: Vec; // nearest point on the line (its centre)
  d: Vec; // unit direction
  a: Vec; // the run's ends
  b: Vec;
  thick: number;
  seg: number; // which straight run of the plan's line work it is
}

const DEG = Math.PI / 180;
const MATCH = Math.cos(20 * DEG); // a line runs "the way of a side" within 20°

const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
const dist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
const unit = (a: Vec): Vec => {
  const l = Math.hypot(a.x, a.y) || 1;
  return { x: a.x / l, y: a.y / l };
};

/**
 * The plan's lines passing within `r` of p, as straight runs (a polyline that turns a corner is
 * two runs, so the corner it makes is found like any other): each at its nearest point to p, with
 * the run's own direction (end to end: steadier than any one pixel step).
 */
function linesNear(engine: IntentEngine, p: Vec, r: number): LocalLine[] {
  const out: LocalLine[] = [];
  const turn = Math.cos(20 * DEG);
  let run = 0;
  for (const id of engine.segsNear(p, r)) {
    const s = engine.plan.segs[id]!;
    const pts = s.pts;
    const n = pts.length / 2;
    const P = (k: number): Vec => ({ x: pts[2 * k]!, y: pts[2 * k + 1]! });
    // split into straight runs where the polyline turns (steps of a pixel or two are no turns)
    let k0 = 0;
    while (k0 < n - 1) {
      let k1 = k0 + 1;
      let dir = unit(sub(P(k1), P(k0)));
      while (k1 < n - 1) {
        const next = sub(P(k1 + 1), P(k1));
        const len = Math.hypot(next.x, next.y);
        if (len > 2.5 && dot(unit(next), dir) < turn) break;
        k1++;
        const whole = sub(P(k1), P(k0));
        if (Math.hypot(whole.x, whole.y) > 3) dir = unit(whole);
      }
      const runLen = Math.hypot(P(k1).x - P(k0).x, P(k1).y - P(k0).y);
      if (runLen >= Math.min(0.6 * r, 12)) {
        let bd = r,
          bq: Vec | null = null;
        for (let k = k0; k < k1; k++) {
          const a = P(k),
            b = P(k + 1);
          const ab = sub(b, a);
          const l2 = dot(ab, ab) || 1;
          const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
          const q = { x: a.x + ab.x * t, y: a.y + ab.y * t };
          const d = dist(p, q);
          if (d < bd) {
            bd = d;
            bq = q;
          }
        }
        if (bq) out.push({ q: bq, d: unit(sub(P(k1), P(k0))), a: P(k0), b: P(k1), thick: s.thick, seg: run });
      }
      run++;
      k0 = k1;
    }
  }
  return out;
}

/** How far x lies beyond the ends of a run (0 when it is alongside it). */
function beyond(l: LocalLine, x: Vec): number {
  const ab = sub(l.b, l.a);
  const t = dot(sub(x, l.a), ab) / (dot(ab, ab) || 1);
  return t < 0 ? -t * Math.hypot(ab.x, ab.y) : t > 1 ? (t - 1) * Math.hypot(ab.x, ab.y) : 0;
}

/** Where a line's outer ink edge is on the `out` side, measured beside the corner (not on the junction's blob). */
function edgeShift(engine: IntentEngine, l: LocalLine, out: Vec, along: Vec): number {
  const thick = Math.min(l.thick, 1.3 * engine.rails.heavy);
  const luma = engine.luma;
  if (luma) {
    // a little way along the side, where the line runs clean
    for (const k of [2.5, 4, 6]) {
      const at = { x: l.q.x + along.x * (k * thick + 3), y: l.q.y + along.y * (k * thick + 3) };
      const e = inkEdge(luma, at, out, thick);
      if (e !== null) return e;
    }
  }
  return edgeOffset(thick);
}

/**
 * The plan's corner for a shape corner at p whose sides leave towards d1 and d2 (unit), within r;
 * failing that, the plan line beside it along one of the sides. Null: nothing there to land on.
 */
export function snapCorner(engine: IntentEngine, p: Vec, d1: Vec, d2: Vec, r: number): Snap | null {
  const lines = linesNear(engine, p, r);
  if (!lines.length) return null;
  const inward = unit({ x: d1.x + d2.x, y: d1.y + d2.y });
  const runs = (l: LocalLine, d: Vec) => Math.abs(dot(l.d, d)) >= MATCH;
  let best: { x: Vec; score: number; a: LocalLine; b: LocalLine } | null = null;
  for (const a of lines) {
    if (!runs(a, d1)) continue;
    for (const b of lines) {
      if (b === a || b.seg === a.seg || !runs(b, d2)) continue;
      const den = cross(a.d, b.d);
      if (Math.abs(den) < Math.sin(25 * DEG)) continue;
      const t = cross(sub(b.q, a.q), b.d) / den;
      const x = { x: a.q.x + a.d.x * t, y: a.q.y + a.d.y * t };
      const dx = dist(x, p);
      // the crossing is near the finger, and each line reaches it (or stops just short: a corner
      // whose lines do not quite meet). A corner the lines really make beats one made by carrying
      // a line on to meet another
      const ea = beyond(a, x),
        eb = beyond(b, x);
      if (dx > r || ea > 0.6 * r || eb > 0.6 * r) continue;
      const score = dx + 2.5 * (ea + eb);
      if (!best || score < best.score) best = { x, score, a, b };
    }
  }
  if (best) {
    const { a, b } = best;
    // each line's outer side: away from where the other side of the shape goes
    const na = { x: -a.d.y, y: a.d.x },
      nb = { x: -b.d.y, y: b.d.x };
    const outA = dot(na, d2) > 0 ? { x: -na.x, y: -na.y } : na;
    const outB = dot(nb, d1) > 0 ? { x: -nb.x, y: -nb.y } : nb;
    const la = { ...a, q: best.x },
      lb = { ...b, q: best.x };
    const ea = edgeShift(engine, la, outA, d1.x * a.d.x + d1.y * a.d.y >= 0 ? a.d : { x: -a.d.x, y: -a.d.y });
    const eb = edgeShift(engine, lb, outB, d2.x * b.d.x + d2.y * b.d.y >= 0 ? b.d : { x: -b.d.x, y: -b.d.y });
    // the two edge lines, and where they cross
    const pa = { x: best.x.x + outA.x * ea, y: best.x.y + outA.y * ea };
    const pb = { x: best.x.x + outB.x * eb, y: best.x.y + outB.y * eb };
    const den = cross(a.d, b.d);
    const t = cross(sub(pb, pa), b.d) / den;
    return { p: { x: pa.x + a.d.x * t, y: pa.y + a.d.y * t }, kind: 'corner' };
  }
  // one line along a side: the corner slides onto its outer edge
  let one: { l: LocalLine; side: Vec; d: number } | null = null;
  for (const l of lines) {
    const side = runs(l, d1) ? d1 : runs(l, d2) ? d2 : null;
    if (!side) continue;
    const d = dist(l.q, p);
    if (!one || d < one.d) one = { l, side, d };
  }
  if (!one) return null;
  const n = { x: -one.l.d.y, y: one.l.d.x };
  const out = dot(n, inward) > 0 ? { x: -n.x, y: -n.y } : n;
  const e = edgeShift(engine, one.l, out, dot(one.side, one.l.d) >= 0 ? one.l.d : { x: -one.l.d.x, y: -one.l.d.y });
  // p moves square onto the edge line (keeping its place along the line)
  const off = dot(sub(p, one.l.q), out);
  return { p: { x: p.x + out.x * (e - off), y: p.y + out.y * (e - off) }, kind: 'line' };
}

/**
 * Where a box's side, carried to q and running `along`, lands on the plan: the plan line running
 * the same way (within 3°) nearest it in reach, the side on that line's outer edge — the side
 * `out` of the box. Null when no such line is near.
 */
export function snapSide(engine: IntentEngine, q: Vec, along: Vec, out: Vec, r: number): Snap | null {
  const par = Math.cos(3 * DEG);
  let one: LocalLine | null = null,
    bd = Infinity;
  for (const l of linesNear(engine, q, r)) {
    if (Math.abs(dot(l.d, along)) < par) continue;
    const d = Math.abs(dot(sub(q, l.q), out));
    if (d < bd) {
      bd = d;
      one = l;
    }
  }
  if (!one) return null;
  const n = { x: -one.d.y, y: one.d.x };
  const o = dot(n, out) >= 0 ? n : { x: -n.x, y: -n.y };
  const e = edgeShift(engine, one, o, dot(along, one.d) >= 0 ? one.d : { x: -one.d.x, y: -one.d.y });
  const off = dot(sub(q, one.q), o);
  return { p: { x: q.x + o.x * (e - off), y: q.y + o.y * (e - off) }, kind: 'line' };
}

/**
 * The circular symbol a tap at p stands inside (a manhole, a pole), looked for out to `maxR`:
 * its centre and radius, when it is at least `minR` across and the tap is within it.
 */
export function circleAt(engine: IntentEngine, p: Vec, maxR: number, minR: number): { o: Vec; r: number } | null {
  for (const r of [maxR / 3, (2 * maxR) / 3, maxR]) {
    const c = circleFit(engine, p, r);
    if (c && c.r >= minR && dist(c.o, p) < c.r) return c;
  }
  return null;
}

/**
 * A round point near a plan line lands on the line's outer edge, the side away from `inside`
 * (a point inside the shape). Null when no line is within r.
 */
export function snapToLine(engine: IntentEngine, p: Vec, inside: Vec, r: number): Snap | null {
  const lines = linesNear(engine, p, r);
  let one: LocalLine | null = null;
  for (const l of lines) if (!one || dist(l.q, p) < dist(one.q, p)) one = l;
  if (!one) return null;
  const n = { x: -one.d.y, y: one.d.x };
  const out = dot(n, sub(inside, one.q)) > 0 ? { x: -n.x, y: -n.y } : n;
  const e = edgeShift(engine, { ...one, q: one.q }, out, one.d);
  return { p: { x: one.q.x + out.x * e, y: one.q.y + out.y * e }, kind: 'line' };
}

/**
 * The centre of the plan's circle a sketched circle (centre c, radius r) was drawn round — a
 * manhole, a pole, a vault's symbol — or null. Only the centre is taken: the circle keeps the size
 * it was drawn. Rays go out from the sketch's centre, noting each place they pass onto ink; the
 * circle through three of those places that most rays agree on (a line crossing the middle is only
 * a few strays) is the symbol — if it is round, seen nearly all round and near the middle.
 */
export function circleCentre(engine: IntentEngine, c: Vec, r: number): Vec | null {
  return circleFit(engine, c, r)?.o ?? null;
}

function circleFit(engine: IntentEngine, c: Vec, r: number): { o: Vec; r: number } | null {
  const luma = engine.luma;
  if (!luma) return null;
  const RAYS = 72;
  // every place each ray passes from paper onto ink (a line crossing the ray is passed by)
  const rays: Vec[][] = [];
  const all: { p: Vec; ray: number }[] = [];
  for (let k = 0; k < RAYS; k++) {
    const a = (k / RAYS) * Math.PI * 2;
    const dx = Math.cos(a),
      dy = Math.sin(a);
    const found: Vec[] = [];
    let wasInk = true;
    const step = Math.max(0.5, r / 300);
    for (let t = 0.15 * r; t <= 1.1 * r && found.length < 4; t += step) {
      const x = c.x + dx * t,
        y = c.y + dy * t;
      if (x < 1 || y < 1 || x >= luma.w - 2 || y >= luma.h - 2) break;
      const ink = lumaAt(luma, x, y) < 128;
      if (ink && !wasInk && t >= 0.2 * r) found.push({ x, y });
      wasInk = ink;
    }
    rays.push(found);
    for (const p of found) all.push({ p, ray: k });
  }
  if (all.length < 3) return null;
  const tol = (R: number) => Math.max(1.5, 0.05 * R);
  // how many rays meet a circle, each by its crossing nearest to it
  const support = (o: Vec, R: number): Vec[] => {
    const out: Vec[] = [];
    for (const f of rays) {
      let best: Vec | null = null,
        bd = tol(R);
      for (const p of f) {
        const d = Math.abs(Math.hypot(p.x - o.x, p.y - o.y) - R);
        if (d <= bd) {
          bd = d;
          best = p;
        }
      }
      if (best) out.push(best);
    }
    return out;
  };
  // a symbol inside the sketch (a sketch drawn tight may just graze it), near its middle
  const fits = (o: Vec, R: number) => R >= 0.2 * r && R <= 1.02 * r && dist(o, c) <= 0.35 * r;
  // the circle through three crossings that the most rays agree on (a fixed sequence of tries)
  let seed = 12345;
  const pick = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return all[seed % all.length]!;
  };
  let bestSet: Vec[] = [];
  for (let trial = 0; trial < 240; trial++) {
    const a = pick(),
      b = pick(),
      d = pick();
    if (a.ray === b.ray || b.ray === d.ray || a.ray === d.ray) continue;
    const o = circumcentre(a.p, b.p, d.p);
    if (!o) continue;
    const R = dist(o, a.p);
    if (!fits(o, R)) continue;
    const set = support(o, R);
    if (set.length > bestSet.length) bestSet = set;
  }
  if (bestSet.length < 0.6 * RAYS) return null;
  // refined on the rays that agree, then once more on its own support
  let fit = fitCircle(bestSet.flatMap((p) => [p.x, p.y]));
  if (fit) {
    const set = support(fit.o, fit.r);
    if (set.length >= 0.6 * RAYS) fit = fitCircle(set.flatMap((p) => [p.x, p.y])) ?? fit;
  }
  if (!fit || fit.rms > 1.5 || !fits(fit.o, fit.r)) return null;
  return { o: fit.o, r: fit.r };
}

function circumcentre(a: Vec, b: Vec, c: Vec): Vec | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-6) return null;
  const a2 = a.x * a.x + a.y * a.y,
    b2 = b.x * b.x + b.y * b.y,
    c2 = c.x * c.x + c.y * c.y;
  return { x: (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d, y: (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d };
}

/** The least-squares circle through points (Kåsa's fit), with its RMS error. */
function fitCircle(pts: readonly number[]): { o: Vec; r: number; rms: number } | null {
  const n = pts.length / 2;
  let mx = 0,
    my = 0;
  for (let i = 0; i < n; i++) {
    mx += pts[2 * i]!;
    my += pts[2 * i + 1]!;
  }
  mx /= n;
  my /= n;
  // centred coordinates keep the normal equations well conditioned
  let suu = 0,
    svv = 0,
    suv = 0,
    suuu = 0,
    svvv = 0,
    suvv = 0,
    svuu = 0;
  for (let i = 0; i < n; i++) {
    const u = pts[2 * i]! - mx,
      v = pts[2 * i + 1]! - my;
    suu += u * u;
    svv += v * v;
    suv += u * v;
    suuu += u * u * u;
    svvv += v * v * v;
    suvv += u * v * v;
    svuu += v * u * u;
  }
  const det = suu * svv - suv * suv;
  if (Math.abs(det) < 1e-9) return null;
  const bu = 0.5 * (suuu + suvv),
    bv = 0.5 * (svvv + svuu);
  const uc = (bu * svv - bv * suv) / det,
    vc = (bv * suu - bu * suv) / det;
  const o = { x: uc + mx, y: vc + my };
  const r = Math.sqrt(uc * uc + vc * vc + (suu + svv) / n);
  let e = 0;
  for (let i = 0; i < n; i++) e += (Math.hypot(pts[2 * i]! - o.x, pts[2 * i + 1]! - o.y) - r) ** 2;
  return { o, r, rms: Math.sqrt(e / n) };
}

/**
 * The box of the plan's lines a tap at p stands inside — a pad, a vault, a manhole box — even
 * when a centreline, a grid line, a symbol or a leader crosses it. From p a ray goes out each way
 * along the box's axes (square to the screen, and square to the nearest long line when that is
 * turned a little) and notes the first few lines running across it (round symbols and lines at an
 * angle are passed over). Every box those lines make round the tap is weighed: each side must be a
 * line the length of the box (3/4 of it; a dashed outline half), and of the boxes that are, the one
 * whose sides stop at its corners wins — an outline's sides end there, while a grid line or a
 * centreline through a pad runs on past it — the smallest first among equals. A box with more than
 * one side running on is no outline at all (a cell of a grid): null, as when there is no box there.
 */
export function enclosingBox(engine: IntentEngine, p: Vec, reach: number, maxSide = Infinity): { cx: number; cy: number; w: number; h: number; rot: number } | null {
  // the line (running along `dir`, within 20°) under a point: where on it, and whether it is dashed
  const lineAt = (q: Vec, dir: Vec): { at: Vec; dashed: boolean } | null => {
    const h = engine.nearest(q, 2.5, (s) => engine.rails.railOf(s.id).loop);
    if (!h) return null;
    // the direction of the piece of line under the point (a polyline may turn a corner just beyond
    // it; a pixel step at a junction is no direction: the longer piece beside it is)
    const pts = h.seg.pts;
    const n = pts.length / 2;
    const piece = (k: number): Vec | null => (k < 0 || k + 1 >= n ? null : sub({ x: pts[2 * k + 2]!, y: pts[2 * k + 3]! }, { x: pts[2 * k]!, y: pts[2 * k + 1]! }));
    let d = piece(h.segIndex)!;
    if (Math.hypot(d.x, d.y) < 3) {
      const a = piece(h.segIndex - 1),
        b = piece(h.segIndex + 1);
      const la = a ? Math.hypot(a.x, a.y) : 0,
        lb = b ? Math.hypot(b.x, b.y) : 0;
      if (Math.max(la, lb) > Math.hypot(d.x, d.y)) d = la >= lb ? a! : b!;
    }
    if (Math.abs(dot(unit(d), dir)) < Math.cos(20 * DEG)) return null;
    return { at: h.point, dashed: engine.rails.railOf(h.seg.id).dashed };
  };
  const turns = [0];
  const near = engine.nearest(p, reach, (s) => engine.rails.railOf(s.id).loop || s.len < 0.25 * reach);
  if (near) {
    const pts = near.seg.pts;
    const d = sub({ x: pts[pts.length - 2]!, y: pts[pts.length - 1]! }, { x: pts[0]!, y: pts[1]! });
    let a = Math.atan2(d.y, d.x);
    a -= Math.round(a / (Math.PI / 2)) * (Math.PI / 2);
    if (Math.abs(a) > 0.5 * DEG && Math.abs(a) < 15 * DEG) turns.push(a);
  }
  let best: { cx: number; cy: number; w: number; h: number; rot: number; score: number } | null = null;
  for (const turn of turns) {
    const u = { x: Math.cos(turn), y: Math.sin(turn) },
      w = { x: -u.y, y: u.x };
    // the first few lines across each way out: how far each is (to its centre)
    const linesOut = (dir: Vec, side: Vec): number[] => {
      const out: number[] = [];
      for (let t = 1; t <= reach && out.length < 5; t += 0.75) {
        const hit = lineAt({ x: p.x + dir.x * t, y: p.y + dir.y * t }, side);
        if (!hit) continue;
        const d = dot(sub(hit.at, p), dir);
        if (!out.length || d - out[out.length - 1]! > 3) out.push(d);
        t = Math.max(t, d + 3); // on past this line
      }
      return out;
    };
    const cand = [linesOut(u, w), linesOut({ x: -u.x, y: -u.y }, w), linesOut(w, u), linesOut({ x: -w.x, y: -w.y }, u)]; // right, left, down, up
    if (cand.some((c) => !c.length)) continue;
    // a side from `from` along `dir` for `len`: is it a line all along, and does that line run on past its ends?
    const seen = new Map<string, { ok: boolean; through: number }>();
    const side = (from: Vec, dir: Vec, len: number): { ok: boolean; through: number } => {
      const key = `${from.x.toFixed(1)},${from.y.toFixed(1)},${dir.x.toFixed(3)},${len.toFixed(1)}`;
      const known = seen.get(key);
      if (known) return known;
      let hit = 0,
        dashed = 0;
      for (let i = 1; i <= 16; i++) {
        const t = 0.1 * len + (0.8 * len * (i - 0.5)) / 16;
        const l = lineAt({ x: from.x + dir.x * t, y: from.y + dir.y * t }, dir);
        if (l) {
          hit++;
          if (l.dashed) dashed++;
        }
      }
      const ok = hit >= 12 || (dashed > hit / 2 && hit >= 8);
      let through = 0;
      for (const [o, sgn] of [[from, -1], [{ x: from.x + dir.x * len, y: from.y + dir.y * len }, 1]] as const) {
        let on = 0;
        for (const k of [0.15, 0.25, 0.35]) {
          const e = Math.max(6, k * len);
          if (lineAt({ x: o.x + dir.x * sgn * e, y: o.y + dir.y * sgn * e }, dir)) on++;
        }
        if (on >= 2) through++;
      }
      const res = { ok, through };
      seen.set(key, res);
      return res;
    };
    for (const r of cand[0]!)
      for (const l of cand[1]!)
        for (const dn of cand[2]!)
          for (const up of cand[3]!) {
            const W = l + r,
              H = up + dn;
            if (Math.max(W, H) > maxSide || Math.min(W, H) < 4) continue;
            const tl = { x: p.x - u.x * l - w.x * up, y: p.y - u.y * l - w.y * up };
            const tr = { x: tl.x + u.x * W, y: tl.y + u.y * W };
            const bl = { x: tl.x + w.x * H, y: tl.y + w.y * H };
            const sides = [side(tl, u, W), side(bl, u, W), side(tl, w, H), side(tr, w, H)];
            if (!sides.every((sd) => sd.ok)) continue;
            // no more than one side may be a line running on past the box (a pad against a long wall)
            if (sides.reduce((a, sd) => a + sd.through, 0) > 2) continue;
            // an outline's sides end at its corners: every end a line runs on past counts against the box
            const score = 1e9 * sides.reduce((a, sd) => a + sd.through, 0) + W * H;
            if (!best || score < best.score) {
              const c = { x: tl.x + u.x * (W / 2) + w.x * (H / 2), y: tl.y + u.y * (W / 2) + w.y * (H / 2) };
              best = { cx: c.x, cy: c.y, w: W, h: H, rot: turn, score };
            }
          }
  }
  if (!best) return null;
  const { score: _score, ...box } = best;
  return box;
}
